import React from 'react';
import type { Theme } from '@mui/material/styles/createTheme';
import type {} from '@mui/x-date-pickers/themeAugmentation';
import { createTheme, ThemeOptions } from '@mui/material/styles';
import merge from 'lodash/merge';
import { ThemeDto } from '@coretava/api/core-service';
import { getThemeFontFamilies } from './font';

export const useLSTheme = (options: {
  theme?: Partial<ThemeOptions>
  dir: string
  coretavaTheme?: ThemeDto
}): Theme => React.useMemo(() => createTheme(merge({
  direction: options.dir,
  palette:
    merge({
      success: {
        main: '#41d6a8',
        light: 'rgba(65, 214, 143,0.1)',
      },
      info: {
        main: '#4b7ccf',
      },
      grey: {
        500: '#e5e6e5',
        600: '#c4c4c4',
        700: '#8e929a',
      },
      background: {
        paper: '#fff',
      },
      text: {
        disabled: '#c7c7c7',
      },
      action: {
        disabledBackground: '#E0E0E0',
      },
    }, options.coretavaTheme?.palette),
  typography: {
    fontSize: 12,
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
    fontFamily: options.coretavaTheme && getThemeFontFamilies(options.coretavaTheme),
    body1: {
      fontSize: '1.2rem',
    },
    body2: {
      fontSize: '1rem',
    },
    caption: {
      fontSize: '0.8rem',
    },
    subtitle1: {
      fontSize: '1.2rem',
    },
  },
  shape: {
    borderRadius: options.coretavaTheme?.card?.borderRadius,
  },
  spacing: (factor: number) => `${0.8 * factor}rem`,
  components:
    merge<ThemeOptions['components'], ThemeOptions['components']>({
      MuiGrid: {
        styleOverrides: {
          root: {
            // This is a workaround for the issue with the Grid component
            // It is adding min-width:0 to the Grid components
            minWidth: 'unset',
          },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          bar: {
            borderRadius: '2.2rem',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          rounded: {
            borderRadius: '0.4rem',
          },
        },
      },
      MuiDatePicker: {
        defaultProps: {
          closeOnSelect: true,
        },
      },
      MuiPickersDay: {
        styleOverrides: {
          root: {
            fontSize: '1rem',
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: '1.2rem',
            margin: '32px 16px',
          },
          paperFullWidth: {
            margin: 0,
            width: 'calc(100% - 1.6rem)',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            boxShadow: '0 5px 10px 0 rgba(0, 0, 0, 0.06)',
            borderRadius: '0.8rem',
          },
        },
      },
      MuiCardContent: {
        styleOverrides: {
          root: {
            '&:last-child': {
              // set bottom padding for card component
              paddingBottom: '1.6rem',
            },
          },
        },
      },
      MuiCardHeader: {
        styleOverrides: {
          root: {
            paddingBottom: 0,
          },
          title: {
            fontWeight: 500,
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'unset',
            padding: '1.2rem',
            borderRadius: 0,
            lineHeight: 1,
            fontSize: '1.2rem',
          },

          textPrimary: {
            color: options.coretavaTheme?.palette?.primary?.main,
          },
          contained: {
            boxShadow: 'unset',
            borderRadius: '0.8rem',
            fontWeight: 500,
            padding: '1rem 2rem',
            lineHeight: '1.7rem',
          },
          outlined: {
            borderRadius: '0.8rem',
            fontWeight: 500,
            padding: '1rem 2rem',
            lineHeight: '1.7rem',
          },
          outlinedPrimary: {
            borderColor: options.coretavaTheme?.palette?.primary?.main,
            color: options.coretavaTheme?.palette?.primary?.main,
          },
          outlinedSecondary: {
            backgroundColor: options.coretavaTheme?.palette?.primary?.main,
            borderColor: options.coretavaTheme?.palette?.text?.secondary,
            color: options.coretavaTheme?.palette?.text?.secondary,
          },
          containedPrimary: {
            color: options.coretavaTheme?.palette?.text?.secondary,
            '&:hover': {
              backgroundColor: options.coretavaTheme?.palette?.primary?.main,
            },
          },
        },
      },
      MuiCircularProgress: {
        styleOverrides: {
          circle: {
            strokeWidth: 2.7,
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            fontSize: '1.5rem',
          },
          input: {
            '&::placeholder': {
              fontSize: '1.2rem',
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: '0.6rem',
            '& input': {
              textAlign: 'center',
            },
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            fontSize: '1.2rem',
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            fontSize: '1.2rem',
            marginLeft: 0,
          },
        },
      },
    }, (options.coretavaTheme?.metadata as {overrides?: ThemeOptions['components']} | undefined)?.overrides),
}, options.theme)), [
  options.coretavaTheme,
  options.dir,
  options.theme,
]);
